import {orientationSteps, orientedDimensions} from '../core/exif-orientation';
import {ImageCodecFailure} from '../core/image-codec';
import type {ImageCodec} from '../core/image-codec';
import type {
  CropRegion,
  Dimensions,
  ExifOrientation,
  OutputLadderStep,
  PixelSample,
  SniffedImage,
} from '../types';
import type {CanvasBackend, Context2D} from './canvas-backend';
import {decodeHeicToImageData} from './heic-decoder';

export type CanvasImage<TCanvas extends CanvasImageSource> = {
  readonly width: number;
  readonly height: number;
  readonly source: CanvasImageSource;
  /** Set once the pixels live on a canvas we own, so sampling and encoding skip a redraw. */
  canvas: TCanvas | null;
  readonly release: () => void;
};

const HEIF_FAMILY: ReadonlySet<SniffedImage['format']> = new Set(['heic', 'avif']);
const WHITE = '#ffffff';

const decodeFailure = (message: string, cause?: unknown): ImageCodecFailure =>
  new ImageCodecFailure('decode-failed', message, {cause});
const encodeFailure = (message: string, cause?: unknown): ImageCodecFailure =>
  new ImageCodecFailure('encode-failed', message, {cause});

const applyOrientation = (
  context: Context2D,
  orientation: ExifOrientation,
  stored: Dimensions,
): void => {
  for (const step of orientationSteps(orientation, stored)) {
    if (step.kind === 'translate') {
      context.translate(step.x, step.y);
    } else if (step.kind === 'rotate') {
      context.rotate((step.quarterTurns * Math.PI) / 2);
    } else {
      context.scale(step.x, step.y);
    }
  }
};

/**
 * The one codec for every runtime: decode, orient, resize, sample, and encode
 * on a canvas. The backend supplies the `OffscreenCanvas` or `<canvas>` calls.
 */
export class CanvasCodec<TCanvas extends CanvasImageSource> implements ImageCodec<
  CanvasImage<TCanvas>
> {
  constructor(private readonly backend: CanvasBackend<TCanvas>) {}

  async decode(blob: Blob, sniff: SniffedImage): Promise<CanvasImage<TCanvas>> {
    try {
      const decoded = await this.backend.decode(blob);
      return {
        width: decoded.width,
        height: decoded.height,
        source: decoded.source,
        canvas: null,
        release: decoded.release,
      };
    } catch (cause) {
      if (!HEIF_FAMILY.has(sniff.format)) {
        throw decodeFailure('Image could not be decoded.', cause);
      }
    }
    const imageData = await decodeHeicToImageData(blob);
    const {canvas, context} = this.blankCanvas(imageData.width, imageData.height);
    context.putImageData(imageData, 0, 0);
    return this.fromCanvas(canvas, imageData.width, imageData.height);
  }

  async transform(
    image: CanvasImage<TCanvas>,
    orientation: ExifOrientation,
  ): Promise<CanvasImage<TCanvas>> {
    const oriented = orientedDimensions(image, orientation);
    const {canvas, context} = this.blankCanvas(oriented.width, oriented.height);
    context.save();
    applyOrientation(context, orientation, image);
    context.drawImage(image.source, 0, 0);
    context.restore();
    return this.fromCanvas(canvas, oriented.width, oriented.height);
  }

  async resize(
    image: CanvasImage<TCanvas>,
    source: CropRegion,
    target: Dimensions,
  ): Promise<CanvasImage<TCanvas>> {
    const native = await this.resizeNatively(image, source, target);
    if (native !== null) {
      return native;
    }
    return this.resizeStepped(image, source, target);
  }

  samplePixels(image: CanvasImage<TCanvas>, tiles: readonly CropRegion[]): readonly PixelSample[] {
    const {context} = this.ensureCanvas(image);
    return tiles.map(tile => {
      const data = context.getImageData(tile.x, tile.y, tile.width, tile.height);
      return {width: data.width, height: data.height, data: data.data};
    });
  }

  async encode(image: CanvasImage<TCanvas>, step: OutputLadderStep): Promise<Blob> {
    const {canvas} = this.ensureCanvas(image);
    const target = step.flattenOnWhite ? this.flattenOnWhite(canvas, image) : canvas;
    let blob: Blob;
    try {
      blob = await this.backend.toBlob(target, step.format, step.quality);
    } catch (cause) {
      throw encodeFailure('Image could not be encoded.', cause);
    }
    if (blob.type !== step.format) {
      throw encodeFailure(`Engine cannot encode ${step.format}.`);
    }
    return blob;
  }

  close(image: CanvasImage<TCanvas>): void {
    image.release();
  }

  private blankCanvas(width: number, height: number): {canvas: TCanvas; context: Context2D} {
    const canvas = this.backend.createCanvas(width, height);
    const context = this.backend.getContext(canvas);
    if (context === null) {
      throw encodeFailure('Canvas 2D context is unavailable.');
    }
    return {canvas, context};
  }

  private fromCanvas(canvas: TCanvas, width: number, height: number): CanvasImage<TCanvas> {
    return {width, height, source: canvas, canvas, release: () => undefined};
  }

  /** Draws the image onto an owned canvas once; later calls reuse it. */
  private ensureCanvas(image: CanvasImage<TCanvas>): {canvas: TCanvas; context: Context2D} {
    if (image.canvas !== null) {
      const context = this.backend.getContext(image.canvas);
      if (context === null) {
        throw encodeFailure('Canvas 2D context is unavailable.');
      }
      return {canvas: image.canvas, context};
    }
    const {canvas, context} = this.blankCanvas(image.width, image.height);
    context.drawImage(image.source, 0, 0);
    image.canvas = canvas;
    return {canvas, context};
  }

  private flattenOnWhite(canvas: TCanvas, image: Dimensions): TCanvas {
    const flat = this.blankCanvas(image.width, image.height);
    flat.context.fillStyle = WHITE;
    flat.context.fillRect(0, 0, image.width, image.height);
    flat.context.drawImage(canvas, 0, 0);
    return flat.canvas;
  }

  /**
   * `resizeQuality: 'high'` is Mitchell on Skia and Lanczos on CoreGraphics.
   * Safari ignores the resize options, so the result is checked and dropped
   * when it is not the requested size.
   */
  private async resizeNatively(
    image: CanvasImage<TCanvas>,
    source: CropRegion,
    target: Dimensions,
  ): Promise<CanvasImage<TCanvas> | null> {
    if (typeof createImageBitmap !== 'function') {
      return null;
    }
    const origin = image.canvas ?? image.source;
    let bitmap: ImageBitmap;
    try {
      bitmap = await createImageBitmap(origin, source.x, source.y, source.width, source.height, {
        resizeWidth: target.width,
        resizeHeight: target.height,
        resizeQuality: 'high',
      });
    } catch {
      return null;
    }
    if (bitmap.width !== target.width || bitmap.height !== target.height) {
      bitmap.close();
      return null;
    }
    return {
      width: bitmap.width,
      height: bitmap.height,
      source: bitmap,
      canvas: null,
      release: () => bitmap.close(),
    };
  }

  /** Halves until within 2× of the target, then one smoothed draw: no single-pass aliasing. */
  private resizeStepped(
    image: CanvasImage<TCanvas>,
    source: CropRegion,
    target: Dimensions,
  ): CanvasImage<TCanvas> {
    let current: CanvasImageSource = image.source;
    let region = source;
    while (region.width > target.width * 2 && region.height > target.height * 2) {
      const half = {width: Math.ceil(region.width / 2), height: Math.ceil(region.height / 2)};
      const {canvas, context} = this.blankCanvas(half.width, half.height);
      context.imageSmoothingQuality = 'high';
      context.drawImage(
        current,
        region.x,
        region.y,
        region.width,
        region.height,
        0,
        0,
        half.width,
        half.height,
      );
      current = canvas;
      region = {x: 0, y: 0, width: half.width, height: half.height};
    }
    const {canvas, context} = this.blankCanvas(target.width, target.height);
    context.imageSmoothingQuality = 'high';
    context.drawImage(
      current,
      region.x,
      region.y,
      region.width,
      region.height,
      0,
      0,
      target.width,
      target.height,
    );
    return this.fromCanvas(canvas, target.width, target.height);
  }
}
